import React, { useState } from 'react'
import Select from 'react-select'
import { Opt, SourceCode } from '../../../constants/carouselOptions'
import formStyles from '../../../styles/forms.module.scss'
import listStyles from '../../../styles/list-table.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import { Button } from '../../Button'
import {
  FilterControl,
  FilterDescriptor,
  descriptorsForSources,
} from './carouselFilterRegistry'
import styles from './CardCarouselEditor.module.scss'

type Filters = Record<string, unknown>

interface CarouselFilterBuilderProps {
  sources: SourceCode[]
  filters: Filters
  onChange: (filters: Filters) => void
}

const hasValue = (v: unknown): boolean => {
  if (v == null) return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') return v.trim().length > 0
  if (typeof v === 'object') return Object.keys(v as object).length > 0
  return true
}

const StartWindowControl: React.FC<{ value: { relative?: string; value?: number }; onChange: (v: unknown) => void }> = ({ value, onChange }) => {
  const UNIT_OPTIONS: Opt[] = [
    { value: 'next_hours', label: 'Next hours' },
    { value: 'next_days', label: 'Next days' },
  ]
  const unit = value.relative ?? 'next_hours'
  return (
    <div className={styles.startWindow}>
      <Select<Opt, false>
        options={UNIT_OPTIONS}
        value={UNIT_OPTIONS.find(o => o.value === unit) ?? null}
        onChange={(o) => {
          // No number yet → don't store a relative-without-value filter ACE
          // would reject or ignore; wait until the user enters a value.
          const rel = o?.value ?? 'next_hours'
          onChange(value.value == null ? {} : { relative: rel, value: value.value })
        }}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
      />
      <input
        type="number"
        min={1}
        value={value.value ?? ''}
        placeholder="e.g. 24"
        onChange={(e) => {
          const n = e.target.value ? parseInt(e.target.value, 10) : undefined
          onChange(n == null || Number.isNaN(n) ? {} : { relative: unit, value: n })
        }}
      />
    </div>
  )
}

const ControlField: React.FC<{ control: FilterControl; value: unknown; onChange: (v: unknown) => void }> = ({ control, value, onChange }) => {
  switch (control.kind) {
    case 'enum-multi': {
      const arr = (value as string[]) ?? []
      return (
        <Select<Opt, true>
          isMulti
          options={control.options}
          value={control.options.filter(o => arr.includes(o.value))}
          onChange={(v) => {
            const next = v.map(o => o.value)
            onChange(next.length ? next : undefined)
          }}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      )
    }
    case 'enum-single': {
      return (
        <Select<Opt, false>
          isClearable
          options={control.options}
          value={control.options.find(o => o.value === value) ?? null}
          onChange={(o) => onChange(o?.value ?? undefined)}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      )
    }
    case 'number':
      return (
        <input
          type="number"
          min={control.min}
          placeholder={control.placeholder}
          value={(value as number) ?? ''}
          onChange={(e) => {
            const n = e.target.value ? parseInt(e.target.value, 10) : undefined
            onChange(n == null || Number.isNaN(n) ? undefined : n)
          }}
        />
      )
    case 'lookup': {
      const Comp = control.Component
      return (
        <Comp
          ids={(value as string[]) ?? []}
          onChange={(ids) => onChange(ids.length ? ids : undefined)}
          placeholder={control.placeholder}
        />
      )
    }
    case 'start-window':
      return <StartWindowControl value={(value as { relative?: string; value?: number }) ?? {}} onChange={onChange} />
  }
}

const CarouselFilterBuilder: React.FC<CarouselFilterBuilderProps> = ({ sources, filters, onChange }) => {
  // Non-core filters the user has revealed via "Add filter" but not yet filled.
  const [revealed, setRevealed] = useState<Set<string>>(new Set())

  const setFilter = (key: string, value: unknown) => {
    const next = { ...filters }
    if (value === undefined) delete next[key]
    else next[key] = value
    onChange(next)
  }

  const removeFilter = (d: FilterDescriptor) => {
    setRevealed(prev => { const s = new Set(prev); s.delete(d.key); return s })
    setFilter(d.key, undefined)
  }

  if (sources.length === 0) {
    return <p className={formStyles.hint}>Select at least one source to configure filters.</p>
  }

  const applicable = descriptorsForSources(sources)
  const shown = applicable.filter(d => d.core || hasValue(filters[d.key]) || revealed.has(d.key))
  const addable = applicable.filter(d => !shown.includes(d))
  const addOptions: Opt[] = addable.map(d => ({ value: d.key, label: d.label }))

  return (
    <div className={styles.filters}>
      <div>
        <div className={`${listStyles.tableHeader} ${styles.filterGrid}`}>
          <div>Filter</div>
          <div>Value</div>
          <div />
        </div>
        <div className={`${listStyles.tableBody} ${listStyles.tableBodyDense}`}>
          {shown.map(d => (
            <div key={d.key} className={`${listStyles.tableRow} ${styles.filterGrid}`}>
              <div className={listStyles.cellPrimary}>
                <p className={listStyles.primaryText}>{d.label}</p>
                {d.hint && <p className={listStyles.secondaryText}>{d.hint}</p>}
              </div>
              <ControlField control={d.control} value={filters[d.key]} onChange={(v) => setFilter(d.key, v)} />
              <div className={listStyles.cellActions}>
                {!d.core && (
                  <Button variant="secondary" size="small" onClick={() => removeFilter(d)} title="Remove filter">✕</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {addOptions.length > 0 && (
        <div className={styles.addFilter}>
          <Select<Opt, false>
            options={addOptions}
            value={null}
            placeholder="+ Add filter…"
            onChange={(o) => o && setRevealed(prev => new Set(prev).add(o.value))}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>
      )}
    </div>
  )
}

export default CarouselFilterBuilder
